import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import {
  Approval as ApprovalIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';
import QuoteApprovalDialog from './QuoteApprovalDialog';

const PendingQuotesPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchPendingQuotes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        status: 'pending',
        sort_by: 'updated_at',
        sort_order: 'ASC',
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const response = await makeApiCall(`api/quotes?${params.toString()}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('quotes.pendingLoadError'));
      }
      setQuotes(data.quotes || []);
      setTotal(data.pagination?.total || 0);
    } catch (loadError) {
      console.error('Failed to load pending quotes:', loadError);
      setError(loadError.message || t('quotes.pendingLoadError'));
    } finally {
      setLoading(false);
    }
  }, [limit, page, search, t]);

  useEffect(() => {
    const timer = setTimeout(fetchPendingQuotes, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchPendingQuotes, search]);

  const openApproval = (quote) => {
    setSelectedQuote(quote);
    setApprovalOpen(true);
  };

  const handleApprovalSuccess = (message) => {
    setApprovalOpen(false);
    setSelectedQuote(null);
    setSnackbar({
      open: true,
      message: message || t('quotes.approvalUpdated'),
    });
    fetchPendingQuotes();
  };

  const formatAwaitingDate = (value) => {
    if (!value) return t('common.notAvailable', 'Not available');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t('common.notAvailable', 'Not available');
    return date.toLocaleString(i18n.language?.startsWith('es') ? 'es-MX' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <Box data-demo-target="page-pending-quotes-root">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#b54708' }}>
              {t('quotes.pendingPageTitle')}
            </Typography>
            <Chip
              label={t('quotes.pendingCount', { count: total })}
              color="warning"
              aria-label={t('quotes.pendingCount', { count: total })}
            />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
            {t('quotes.pendingPageIntro')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPendingQuotes}
          disabled={loading}
          aria-label={t('quotes.refreshPending')}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          {t('quotes.refreshPending')}
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          label={t('common.search')}
          placeholder={t('quotes.searchPendingPlaceholder')}
          inputProps={{ 'aria-label': t('quotes.searchPendingPlaceholder') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      ) : quotes.length === 0 ? (
        <Alert severity="success" variant="outlined">
          {search.trim() ? t('quotes.noPendingSearchResults') : t('quotes.noPendingQuotes')}
        </Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {quotes.map((quote) => {
              const customerName = quote.customer_name
                || quote.company_name
                || `${quote.first_name || ''} ${quote.last_name || ''}`.trim()
                || t('quotes.unnamedCustomer');
              const address = [quote.street_address, quote.city, quote.province]
                .filter(Boolean)
                .join(', ');

              return (
                <Grid key={quote.id} size={{ xs: 12, md: 6, xl: 4 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderTop: '4px solid',
                      borderTopColor: 'warning.main',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Box>
                          <Typography variant="overline" color="text.secondary">
                            {t('quotes.quoteNumber')}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {quote.quote_number || `#${quote.id}`}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {formatCurrencyWithSymbol(quote.total_amount || 0)}
                        </Typography>
                      </Stack>

                      <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
                        {customerName}
                      </Typography>
                      {address && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {address}
                        </Typography>
                      )}

                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('quotes.awaitingSince')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatAwaitingDate(quote.updated_at || quote.created_at)}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        startIcon={<ApprovalIcon />}
                        onClick={() => openApproval(quote)}
                        aria-label={t('quotes.reviewPendingQuoteAria', {
                          quote: `${quote.quote_number || quote.id} — ${customerName}`,
                        })}
                      >
                        {t('quotes.reviewQuote')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              setLimit(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[6, 12, 24]}
            labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
            sx={{ mt: 2 }}
          />
        </>
      )}

      <QuoteApprovalDialog
        open={approvalOpen}
        onClose={() => {
          setApprovalOpen(false);
          setSelectedQuote(null);
        }}
        quote={selectedQuote}
        onSuccess={handleApprovalSuccess}
        userRole={user?.user_type}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default PendingQuotesPage;
